import React from "react";

import { floor } from "~/tools/floor";
import { Impulse, useScoped, useScopedMemo } from "~/tools/impulse";
import { min } from "~/tools/min";
import { usePermanent } from "~/tools/use-permanent";

import { Link } from "../react/link";

import { TimeSeriesChart, type TimeSeriesChartProps } from "./TimeSeriesChart";

export interface PaginatedTimeSeriesChartProps extends Omit<TimeSeriesChartProps, "maxVisibleTimeSeries"> {
	pageSize: number;
	renderTitle?: (timeSeriesCount: number) => React.ReactNode;
}

export const PaginatedTimeSeriesChart: React.FC<PaginatedTimeSeriesChartProps> = ({
	pageSize,
	timeSeries,
	renderTitle,
	...props
}) => {
	const pageIndex = usePermanent(() => Impulse(0));

	const sortedTimeSeries = React.useMemo(() => {
		// it should display the longest time series first, otherwise the chart might have
		// weird gaps due do few values in first VISIBLE time series
		return timeSeries.slice().sort((left, right) => right.values.length - left.values.length);
	}, [timeSeries]);

	const visibleTimeSeries = useScopedMemo(
		(scope) => {
			const startIndex = pageIndex.getValue(scope) * pageSize;
			const endIndex = startIndex + pageSize;

			return sortedTimeSeries.slice(startIndex, endIndex);
		},
		[pageIndex, pageSize, sortedTimeSeries],
	);

	// if the time series length changes - adjust the page index, so that it doesn't go out of bounds
	React.useEffect(() => {
		pageIndex.setValue((index) => {
			const pageCount = floor(sortedTimeSeries.length / pageSize, 0);

			return min(index, pageCount);
		});
	}, [pageIndex, pageSize, sortedTimeSeries.length]);

	return (
		<div className="flex h-full w-full flex-col space-y-4">
			<PaginationHeader
				pageIndex={pageIndex}
				pageSize={pageSize}
				renderTitle={renderTitle}
				timeSeriesCount={sortedTimeSeries.length}
			/>

			<TimeSeriesChart {...props} timeSeries={visibleTimeSeries} />
		</div>
	);
};

const PaginationHeader: React.FC<{
	timeSeriesCount: number;
	pageSize: number;
	pageIndex: Impulse<number>;
	renderTitle: undefined | ((timeSeriesCount: number) => React.ReactNode);
}> = ({ pageSize, timeSeriesCount, pageIndex, renderTitle }) => {
	const isPaginationVisible = timeSeriesCount > pageSize;

	const pageIndexValue = useScoped(pageIndex);
	const prevTimeSeriesCount = pageIndexValue > 0 ? pageSize : 0;
	const nextTimeSeriesCount = min(pageSize, timeSeriesCount - (pageIndexValue + 1) * pageSize);
	const currentTimeSeriesCount = min(pageSize, timeSeriesCount - pageIndexValue * pageSize);

	if (renderTitle == null && !isPaginationVisible) {
		return null;
	}

	return (
		<header className="flex flex-row gap-2">
			<h3 className="m-0 flex-1 font-medium text-13 text-gray-800 leading-5">{renderTitle?.(timeSeriesCount)}</h3>

			{isPaginationVisible && (
				<div className="flex gap-x-4">
					{prevTimeSeriesCount > 0 ? (
						<Link el="button" onClick={() => pageIndex.setValue((index) => index - 1)} theme="primary" type="button">
							View previous {prevTimeSeriesCount}
						</Link>
					) : (
						<div>Viewing first {currentTimeSeriesCount} time series</div>
					)}

					<span className="border-0 border-gray-400 border-l border-solid" />

					{nextTimeSeriesCount > 0 ? (
						<Link el="button" onClick={() => pageIndex.setValue((index) => index + 1)} theme="primary" type="button">
							View next {nextTimeSeriesCount}
						</Link>
					) : (
						<div>Viewing last {currentTimeSeriesCount} time series</div>
					)}
				</div>
			)}
		</header>
	);
};
